import {getCurrencySymbol} from '@functions/helpers/getCurrencySymbol';
import {useCallback, useContext, useEffect, useState} from 'react';
import {DEFAULT_MESSAGE_TYPE} from '@functions/helpers/defaultCampaignSetting';
import formatCurrencyValue from '@functions/helpers/currency/formatCurrencyValue';
import getUrlSearch from '@functions/helpers/getUrlSearch';
import {FloatButtonContext} from '@assets/contexts/floatButtonContext';

const createProductObject = productData => {
  if (!productData) return null;

  const firstVariant = productData.variants?.[0];
  const currency = window?.Shopify?.currency?.active;

  return {
    firstVariantId: firstVariant?.id?.split('/').pop(),
    name: productData.title || productData.name,
    price:
      getCurrencySymbol(currency) +
      ' ' +
      formatCurrencyValue(productData.priceRange.amount || firstVariant.price),
    img: productData.images?.[0]?.url || productData.img,
    onlineStoreUrl:
      productData.onlineStoreUrl || `https://${window.Shopify?.shop}/products/${productData.handle}`
  };
};

const useProductRecommendation = ({campaign, isPreview = false}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [fetchedRecommendedProduct, setFetchedRecommendedProduct] = useState(false);

  const {shop: shopDomain, country} = window.Shopify || {};
  const {type: messageType, productType} = campaign?.message || {};
  const {getData} = useContext(FloatButtonContext);

  const fetchRecommendedProduct = useCallback(async () => {
    try {
      const viewingProductId = window?.ShopifyAnalytics?.meta?.product?.gid;
      if (!viewingProductId || !shopDomain || !country) return;

      const res = await getData(
        `/products/recommended?${getUrlSearch({
          shop: shopDomain,
          limit: 10,
          type: productType,
          country
        })}`
      );

      const product = createProductObject(res.data);
      if (product) setSelectedProduct(product);
    } catch (err) {
      console.error('Error fetching recommended product:', err);
    } finally {
      setFetchedRecommendedProduct(true);
    }
  }, [productType, shopDomain, country]);

  const handleCustomProduct = useCallback(async () => {
    try {
      const viewingProductId = window?.ShopifyAnalytics?.meta?.product?.gid;
      const campaignProductsList = campaign?.message?.productsList || [];
      const productsList = campaignProductsList.filter(product => product.id != viewingProductId);
      if (!productsList.length || !shopDomain || !country) return;

      const randomProduct = productsList[Math.floor(Math.random() * productsList.length)];
      const res = await getData(
        `/products/recommended?${getUrlSearch({
          shop: shopDomain,
          limit: 10,
          type: 'custom',
          productId: randomProduct.id,
          country
        })}`
      );
      const product = createProductObject(res.data);
      if (product) setSelectedProduct(product);
    } catch (e) {
      console.error(e);
    } finally {
      setFetchedRecommendedProduct(true);
    }
  }, [campaign?.message?.productsList, shopDomain, country]);

  const handleSimilarProducts = useCallback(async () => {
    try {
      const {shop: shopDomain, country} = window.Shopify || {};
      const viewingProductId = window?.ShopifyAnalytics?.meta?.product?.gid;
      if (!shopDomain || !country || !viewingProductId) return;

      const res = await getData(
        `/products/recommended?${getUrlSearch({
          shop: shopDomain,
          // limit: 10,
          type: 'similar_products',
          productId: viewingProductId,
          country
        })}`
      );

      const product = createProductObject(res.data);
      if (product) setSelectedProduct(product);
    } catch (e) {
      console.error(e);
    } finally {
      setFetchedRecommendedProduct(true);
    }
  }, [country]);

  useEffect(() => {
    if (messageType === DEFAULT_MESSAGE_TYPE.PRODUCT_RECOMMENDATION) {
      if (isPreview) {
        const exampleProduct = {
          // firstVariantId: '41573612126302',
          name: 'Example Product',
          price: '$69.99',
          img:
            'https://cdn.shopify.com/s/files/1/0914/6757/9762/files/paul-shirt-in-grey-flannel-hanashop-com-vn-2.jpg?v=1747987352'
          // onlineStoreUrl:
          //   'https://ag-harry-chatty-local2.myshopify.com/products/bedside-table-cefce'
        };
        setSelectedProduct(exampleProduct);
        return;
      }

      switch (productType) {
        case 'best_sellers':
        case 'new_arrivals':
          return fetchRecommendedProduct();
        case 'similar_products':
          return handleSimilarProducts();
        case 'custom':
          return handleCustomProduct();
      }
    }
  }, [
    messageType,
    productType,
    fetchRecommendedProduct,
    handleCustomProduct,
    handleSimilarProducts
  ]);

  return {selectedProduct, fetchedRecommendedProduct};
};

export default useProductRecommendation;
